<div class="container py-4">
  <!-- Back Button -->
  <button
    *ngIf="domainService.isCommunityDomain()"
    class="btn btn-outline-primary mb-3 d-flex align-items-center gap-2 btn-lite-blue"
    (click)="goBack()"
  >
    <i class="bi bi-arrow-left"></i> Back
  </button>

  <h2 class="mb-3 section-title titleList">Applicants</h2>

  <!-- Job summary (from response.body.post) -->
  <div *ngIf="post as p" class="card shadow-sm border-0 mb-4">
    <div class="card-body">
      <ng-container *ngIf="parseJob(p.content) as job">
        <div class="d-flex align-items-start justify-content-between">
          <div>
            <h5 class="mb-1">{{ job.title || 'Job' }}</h5>
            <div class="text-muted small">Posted {{ p.created_at | date: 'mediumDate' }}</div>
          </div>
          <span class="badge bg-warning text-dark" *ngIf="p.post_type === 'job'">Job</span>
        </div>

        <div class="mt-3" *ngIf="job.description">
          <div class="fw-semibold mb-1">Description</div>
          <div class="text-body">{{ job.description }}</div>
        </div>

        <div class="row row-cols-1 row-cols-md-3 g-2 mt-2 text-muted small">
          <div *ngIf="job.location" class="d-flex align-items-center">
            <i class="bi bi-geo-alt me-2"></i><strong class="me-1">Location:</strong> {{ job.location }}
          </div>
          <div *ngIf="job.startDate" class="d-flex align-items-center">
            <i class="bi bi-calendar-event me-2"></i><strong class="me-1">Start date:</strong> {{ job.startDate }}
          </div>
          <div *ngIf="job.duration" class="d-flex align-items-center">
            <i class="bi bi-hourglass-split me-2"></i><strong class="me-1">Duration:</strong> {{ job.duration }}
          </div>
        </div>
      </ng-container>
    </div>
  </div>

  <!-- Loading -->
  <div *ngIf="isLoading" class="text-center my-5">
    <div class="spinner-border text-primary" role="status"></div>
  </div>

  <!-- Pagination Top (always visible when not loading) -->
  <nav *ngIf="!isLoading" class="d-flex justify-content-center my-3">
    <ul class="pagination mb-0">
      <li class="page-item" [class.disabled]="currentPage === 1">
        <a class="page-link" (click)="onPageChange(currentPage - 1)">‹</a>
      </li>
      <li class="page-item" *ngFor="let page of pages" [class.active]="currentPage === page">
        <a class="page-link" (click)="onPageChange(page)">{{ page }}</a>
      </li>
      <li class="page-item" [class.disabled]="currentPage === pages[pages.length - 1]">
        <a class="page-link" (click)="onPageChange(currentPage + 1)">›</a>
      </li>
    </ul>
    <span class="ms-3 align-self-center text-muted small">
      Page {{ currentPage }} of {{ pages[pages.length - 1] }}
    </span>
  </nav>

  <!-- Showing X–Y of Z applicants -->
  <div *ngIf="!isLoading && totalApplicants > 0" class="text-end text-muted small mb-2">
    Showing {{ showingFrom }}–{{ showingTo }} of {{ totalApplicants }} applicants
  </div>

  <!-- Table -->
  <div *ngIf="!isLoading && applicants.length > 0" class="table-responsive">
    <table class="table table-hover align-middle shadow-sm">
      <thead class="custom-thead">
        <tr>
          <th></th>
          <th>Designation</th>
          <th>Nationality</th>
          <th>Availability</th>
          <th>Last Active</th>
          <th style="width: 420px;">Application Message</th>
          <th>Applied At</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let user of applicants">
          <!-- Avatar + Name -->
          <td>
            <div class="d-flex align-items-center gap-2">
              <img
                [src]="user.avatar || 'https://kontact-app-public.s3-ap-southeast-1.amazonaws.com/images/Sign-Up-Image.jpg'"
                class="rounded-circle"
                width="40"
                height="40"
                style="object-fit: cover; cursor: pointer"
                (click)="openProfile(user)"
                [title]="user.name"
              />
              <span class="fw-semibold userName" style="cursor: pointer" (click)="openProfile(user)">
                {{ user.name || '-' }}
              </span>
            </div>
          </td>

          <!-- Designation -->
          <td>
            <span class="text-dark fw-normal">{{ user.designation || '–' }}</span>
          </td>

          <!-- Nationality (respect public/private) -->
          <td>
            <span *ngIf="user.is_public; else hiddenNationality">
              {{ user.nationality || '-' }}
            </span>
            <ng-template #hiddenNationality>
              <span class="text-muted d-flex align-items-center gap-1" title="User has chosen to keep this private">
                <span style="font-size: 1.1rem;">👁️‍🗨️</span> Private
              </span>
            </ng-template>
          </td>

          <!-- Availability -->
          <td>
            <!-- Recruiter badge -->
            <ng-container *ngIf="user.is_recruiter; else seekerAvailability">
              <span
                class="badge availability-badge"
                [ngClass]="(user.recruiter_status || 'PENDING').toUpperCase() === 'APPROVED'
                           ? 'bg-primary text-white'
                           : 'bg-info text-dark'"
                [title]="'Recruiter ' + (user.recruiter_status || 'PENDING')"
              >
                Recruiter
              </span>
            </ng-container>

            <!-- Seeker availability -->
            <ng-template #seekerAvailability>
              <span *ngIf="user.availability" [ngClass]="getAvailabilityBadgeClass(user.availability)" class="badge">
                {{ getAvailabilityStatus(user.availability) }}
              </span>
              <span *ngIf="!user.availability" class="text-muted">–</span>
            </ng-template>
          </td>

          <!-- Last Active -->
          <td>{{ user.last_active_readable || '-' }}</td>

          <!-- Application Message (scrollable) -->
          <td style="width: 420px;">
            <ng-container *ngIf="user.application as app; else noAppMsg">
              <div
                class="border rounded p-2 bg-light"
                style="max-height: 120px; overflow: auto; white-space: pre-wrap; word-break: break-word;"
                [attr.aria-label]="'Application message from ' + (user.name || 'applicant')"
              >
                {{ app.content || '—' }}
              </div>
              <div *ngIf="app.attachments?.length" class="text-muted small mt-1">
                <span class="badge bg-light text-dark">
                  {{ app.attachments.length }} attachment{{ app.attachments.length === 1 ? '' : 's' }}
                </span>
              </div>
            </ng-container>
            <ng-template #noAppMsg>—</ng-template>
          </td>

          <!-- Applied At -->
          <td>
            <ng-container *ngIf="user.application as app; else noAppAt">
              {{ app.created_at | date: 'medium' }}
            </ng-container>
            <ng-template #noAppAt>—</ng-template>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- No Applicants Fallback -->
  <div *ngIf="!isLoading && applicants.length === 0" class="text-muted text-center my-5">
    No applicants found.
  </div>

  <!-- Pagination Bottom (always visible when not loading) -->
  <nav *ngIf="!isLoading" class="d-flex justify-content-center my-3">
    <ul class="pagination mb-0">
      <li class="page-item" [class.disabled]="currentPage === 1">
        <a class="page-link" (click)="onPageChange(currentPage - 1)">‹</a>
      </li>
      <li class="page-item" *ngFor="let page of pages" [class.active]="currentPage === page">
        <a class="page-link" (click)="onPageChange(page)">{{ page }}</a>
      </li>
      <li class="page-item" [class.disabled]="currentPage === pages[pages.length - 1]">
        <a class="page-link" (click)="onPageChange(currentPage + 1)">›</a>
      </li>
    </ul>
    <span class="ms-3 align-self-center text-muted small">
      Page {{ currentPage }} of {{ pages[pages.length - 1] }}
    </span>
  </nav>
</div>

<!-- Profile Modal -->
<ng-template #profileModal let-modal>
  <div class="modal-header border-0 pb-0">
    <h5 class="modal-title fw-semibold">
      {{ selectedUser?.is_public ? selectedUser?.name : 'Private User' }}
    </h5>
    <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
  </div>

  <div class="modal-body">
    <div class="d-flex flex-column align-items-center text-center">
      <img
        [src]="selectedUser?.is_public && selectedUser?.avatar_url ? selectedUser.avatar_url : 'logo/logo.png'"
        class="rounded-circle shadow mb-3"
        style="width: 100px; height: 100px;"
      />

      <ng-container *ngIf="selectedUser?.is_public; else privateProfile">
        <p class="mb-2">
          <i class="bi bi-envelope-fill me-2 text-primary"></i>
          <strong>Email:</strong> {{ selectedUser?.email }}
        </p>

        <!-- Phone + WhatsApp + Call -->
        <p *ngIf="selectedUser?.phone" class="mb-2 d-flex align-items-center gap-2 flex-wrap justify-content-center">
          <span>
            <i class="bi bi-telephone-fill me-2 text-success"></i>
            <strong>Phone:</strong> {{ selectedUser.phone }}
          </span>
          <a
            class="btn btn-sm btn-success"
            [href]="getWhatsAppLink(selectedUser.phone)"
            target="_blank"
            rel="noopener"
            title="Chat on WhatsApp"
          >
            <i class="bi bi-whatsapp"></i> WhatsApp
          </a>
          <a
            class="btn btn-sm btn-outline-primary"
            [href]="getTelLink(selectedUser.phone)"
            title="Call"
          >
            <i class="bi bi-telephone"></i> Call
          </a>
        </p>

        <p *ngIf="selectedUser?.about_me" class="mb-2">
          <i class="bi bi-person-lines-fill me-2 text-info"></i>
          <strong>About Me:</strong><br />
          <span class="d-block mt-1">{{ selectedUser.about_me }}</span>
        </p>

        <!-- Availability -->
        <div *ngIf="selectedUser?.availability" class="mb-3">
          <p class="mb-0">
            <strong>Availability:</strong>
            {{ getAvailabilityStatus(selectedUser.availability) }}
          </p>
        </div>

        <!-- Resume Viewer -->
        <div *ngIf="resumeViewerUrl; else noResume" class="w-100 mt-2">
          <p class="fw-semibold mb-2">
            <i class="bi bi-file-earmark-text me-1"></i> Resume
          </p>
          <div class="border rounded shadow-sm overflow-hidden" style="height: 500px;">
            <ngx-doc-viewer
              [url]="resumeViewerUrl"
              [viewer]="'google'"
              style="width: 100%; height: 100%; border: none;"
            ></ngx-doc-viewer>
          </div>
          <div class="mt-2">
            <a
              [href]="resumeViewerUrl"
              target="_blank"
              rel="noopener"
              class="btn btn-sm btn-outline-primary"
            >
              <i class="bi bi-box-arrow-up-right"></i> Open Resume in new tab
            </a>
          </div>
        </div>
        <ng-template #noResume>
          <p class="text-muted fst-italic mt-2">No resume provided.</p>
        </ng-template>
      </ng-container>

      <ng-template #privateProfile>
        <div class="alert alert-warning w-100 mt-2 text-center">
          This profile is private.
        </div>
      </ng-template>
    </div>
  </div>
</ng-template>
